// 管理画面のログイン・ログアウト・パスワード変更（REST）。
// セッションはサーバーが HttpOnly Cookie で管理するので、ここではトークンを扱わない（localStorage にも置かない）。

// ロール。server_admin = サーバー管理者（開発者アカウントの管理もできる）、admin = 管理者
export type AdminRole = "server_admin" | "admin";

export interface AdminSessionInfo {
  admin_user_id: string;
  mail: string;
  admin_name: string;
  is_first_login: boolean;
  role: AdminRole;
  role_label: string;
  session_expires_at: string;
}

async function call<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AdminAuthError(res.status, data?.error ?? "通信に失敗しました");
  }
  return data as T;
}

export class AdminAuthError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export const AdminAuthAPI = {
  login: (mail: string, password: string) =>
    call<AdminSessionInfo>("POST", "/api/admin/auth/login", { mail, password }),
  logout: () => call<{ ok: boolean }>("POST", "/api/admin/auth/logout"),
  // ログインしていなければ null
  session: async (): Promise<AdminSessionInfo | null> => {
    try {
      return await call<AdminSessionInfo>("GET", "/api/admin/auth/session");
    } catch (e) {
      if (e instanceof AdminAuthError && e.status === 401) return null;
      throw e;
    }
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    call<AdminSessionInfo>("POST", "/api/admin/auth/password", {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// 管理画面の API（Connect の AdminService / AdminCRUDService と /api/admin/*・/api/preview/*）
function isAdminApiPath(path: string): boolean {
  return (
    path.startsWith("/service.AdminService/") ||
    path.startsWith("/service.AdminCRUDService/") ||
    (path.startsWith("/api/admin/") && !path.startsWith("/api/admin/auth/")) ||
    path.startsWith("/api/preview/")
  );
}

/**
 * セッション切れ（401）を検知したらログイン画面に戻すため、fetch を 1 か所で見張る。
 * 生成コードの API クライアントはエラーを文字列にして返すだけで状態コードが失われるので、ここで拾う。
 * connect-web は呼び出しのたびに globalThis.fetch を参照するので、差し替えが効く。
 */
export function installUnauthorizedHandler(onUnauthorized: () => void): () => void {
  const original = window.fetch;
  const wrapped: typeof window.fetch = async (input, init) => {
    const res = await original(input, init);
    if (res.status === 401) {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      if (isAdminApiPath(new URL(url, window.location.origin).pathname)) {
        onUnauthorized();
      }
    }
    return res;
  };
  window.fetch = wrapped;
  return () => {
    window.fetch = original;
  };
}
